

class Navbar extends HTMLElement {
  constructor () {
    super()
    this.initialized = false
  }

  connectedCallback () {
    if (this.initialized) return
    this.initialized = true

    this.handleDropdownsBackdrop()
    this.enableHoverDropdowns()
    this.guardHoverAfterSelectClose()
    this.handleHeaderReveal()
    this.adjustOffcanvasMenu()
    this.handleOffcanvasOnResize()
  }

  adjustOffcanvasMenu () {
    const offcanvasMenu = document.querySelector('#offcanvas-menu')
    const navbarMobile = document.querySelector('#navbar-mobile')
    const headerGroup = document.querySelector('#header-group')
    const documentElement = document.documentElement
    const mobileMq = window.matchMedia('(max-width: 1199.98px)')
    const offcanvasBody = offcanvasMenu?.querySelector('.offcanvas-body')
    const offcanvasMenuToggles = document.querySelectorAll('[href="#offcanvas-menu"][data-bs-toggle="offcanvas"], [data-bs-target="#offcanvas-menu"][data-bs-toggle="offcanvas"]')
    let syncRafId = null
    let bodyScrollLockY = 0
    let lockedTouchStartY = 0

    if (offcanvasMenu && offcanvasMenu.parentElement !== document.body) {
      document.body.appendChild(offcanvasMenu)
    }

    const setOffcanvasMenuReadyState = (isReady) => {
      documentElement.classList.toggle('offcanvas-menu-ready', isReady)
      offcanvasMenuToggles.forEach((toggleEl) => {
        toggleEl.classList.toggle('offcanvas-menu-toggle-pending', !isReady)
        if (isReady) {
          toggleEl.removeAttribute('aria-disabled')
        } else {
          toggleEl.setAttribute('aria-disabled', 'true')
        }
      })
    }

    setOffcanvasMenuReadyState(false)

    const resetOffcanvasMenuCollapses = () => {
      if (!offcanvasMenu) return

      offcanvasMenu.querySelectorAll('.collapsing, .collapse').forEach((collapseEl) => {
        const instance = window.bootstrap?.Collapse?.getInstance?.(collapseEl)
        if (instance?.dispose) instance.dispose()

        collapseEl.classList.remove('show', 'collapsing')
        collapseEl.classList.add('collapse')
        collapseEl.style.height = ''
        collapseEl.style.display = ''
      })

      offcanvasMenu
        .querySelectorAll('[data-bs-toggle="collapse"][aria-expanded="true"]')
        .forEach((triggerEl) => triggerEl.setAttribute('aria-expanded', 'false'))
    }

    const clearOffcanvasMenuLayout = () => {
      if (!offcanvasMenu) return

      documentElement.style.removeProperty('--offcanvas-menu-top')
      offcanvasMenu.style.removeProperty('--offcanvas-menu-top')
      offcanvasMenu.style.top = ''
      offcanvasMenu.style.bottom = ''
      offcanvasMenu.style.height = ''
      offcanvasMenu.style.maxHeight = ''
    }

    const syncOffcanvasMenuLayout = () => {
      // The menu is a CSS-positioned full-height side panel at every width
      // below 1200px (mockup option 1c) — only clear any leftover inline
      // below-header layout (old format, bfcache restores).
      clearOffcanvasMenuLayout()
    }

    const scheduleOffcanvasMenuLayoutSync = () => {
      if (syncRafId) {
        window.cancelAnimationFrame(syncRafId)
      }

      syncOffcanvasMenuLayout()

      syncRafId = window.requestAnimationFrame(() => {
        syncOffcanvasMenuLayout()
        syncRafId = window.requestAnimationFrame(() => {
          syncOffcanvasMenuLayout()
          syncRafId = null
        })
      })
    }

    const lockPageScroll = () => {
      if (!mobileMq.matches || document.body.classList.contains('offcanvas-menu-scroll-locked')) return

      bodyScrollLockY = window.scrollY || window.pageYOffset || 0
      // While locked, CSS switches #header-group to position:fixed so it stays
      // pinned behind the veil. On tablet the header is position:sticky (in the
      // normal flow), so removing it from the flow yanks #main — and the
      // background seen through the backdrop — upward by the header height: the
      // visible "jump". Reserve that space with a padding-top on <body> so the
      // content stays put. Measured before the header goes fixed.
      // getBoundingClientRect (not offsetHeight) so the fractional height is kept
      // — an integer-rounded reserve leaves a sub-pixel vertical jump on the page.
      const headerHeight = headerGroup ? headerGroup.getBoundingClientRect().height : 0
      // Horizontal shift from the scrollbar disappearing is handled globally by
      // `html { scrollbar-gutter: stable }` (custom.css) — the gutter stays
      // reserved while locked, so nothing sized against the viewport moves.
      documentElement.classList.add('offcanvas-menu-scroll-locked')
      documentElement.style.overflow = 'hidden'
      documentElement.style.overscrollBehavior = 'none'
      document.body.classList.add('offcanvas-menu-scroll-locked')
      document.body.style.position = 'fixed'
      document.body.style.top = `-${bodyScrollLockY}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
      document.body.style.width = '100%'
      // Reserve the header's height ONLY where the header is actually pulled out
      // of the flow while locked. Below 768px, sections.css pins the header with
      // `position: static !important` (the announcement bar scrolls away on
      // phones), so the lock's `position: fixed` never wins — the header STAYS in
      // the flow. Adding padding-top there is pure extra space and shoves the
      // page down by the header height: the "jump" seen on phones at tap time.
      // Without it the body position:fixed lock is a pure translation (no jump).
      // On tablet (>=768px) the header does leave the flow, so the reserve is
      // still needed to keep the content from moving up — except on blog articles,
      // where custom.css keeps it in the flow too. Hence reading the real position.
      const headerLeavesFlowWhenLocked = headerGroup && window.getComputedStyle(headerGroup).position === 'fixed'
      if (headerHeight > 0 && headerLeavesFlowWhenLocked) {
        document.body.style.paddingTop = `${headerHeight}px`
      }
      document.body.style.overflow = 'hidden'
      document.addEventListener('touchstart', handleLockedTouchStart, { passive: true, capture: true })
      document.addEventListener('touchmove', handleLockedTouchMove, { passive: false, capture: true })
      document.addEventListener('wheel', handleLockedWheel, { passive: false, capture: true })
    }

    const unlockPageScroll = () => {
      if (!document.body.classList.contains('offcanvas-menu-scroll-locked')) return

      documentElement.classList.remove('offcanvas-menu-scroll-locked')
      documentElement.style.overflow = ''
      documentElement.style.overscrollBehavior = ''
      document.body.classList.remove('offcanvas-menu-scroll-locked')
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      document.body.style.paddingTop = ''
      document.body.style.overflow = ''
      document.removeEventListener('touchstart', handleLockedTouchStart, true)
      document.removeEventListener('touchmove', handleLockedTouchMove, true)
      document.removeEventListener('wheel', handleLockedWheel, true)
      // Removing position:fixed drops the document back to scrollY 0, then we
      // restore the saved position. Bootstrap's Reboot sets scroll-behavior:
      // smooth on :root, which would ANIMATE that restore — the page visibly
      // jumps to the top and glides back down. Force the jump to be instant.
      const previousScrollBehavior = documentElement.style.scrollBehavior
      documentElement.style.scrollBehavior = 'auto'
      window.scrollTo(0, bodyScrollLockY)
      documentElement.style.scrollBehavior = previousScrollBehavior
    }

    const getScrollableTouchContainer = (target) => {
      if (!offcanvasBody || !(target instanceof Element) || !offcanvasBody.contains(target)) return null

      let current = target
      while (current && current !== offcanvasBody) {
        if (current.scrollHeight > current.clientHeight + 1) {
          const overflowY = window.getComputedStyle(current).overflowY
          if (overflowY === 'auto' || overflowY === 'scroll') return current
        }
        current = current.parentElement
      }

      return offcanvasBody
    }

    const handleLockedTouchStart = (event) => {
      lockedTouchStartY = event.touches?.[0]?.clientY ?? 0
    }

    const canScrollContainer = (scrollContainer, deltaY) => {
      if (!scrollContainer) return false
      if (scrollContainer.scrollHeight <= scrollContainer.clientHeight + 1) return false

      const isPullingDown = deltaY > 0
      const isPushingUp = deltaY < 0
      const isAtTop = scrollContainer.scrollTop <= 0
      const isAtBottom = Math.ceil(scrollContainer.scrollTop + scrollContainer.clientHeight) >= scrollContainer.scrollHeight

      if ((isAtTop && isPullingDown) || (isAtBottom && isPushingUp)) return false
      return true
    }

    const handleLockedTouchMove = (event) => {
      if (!document.body.classList.contains('offcanvas-menu-scroll-locked')) return

      const scrollContainer = getScrollableTouchContainer(event.target)
      const currentY = event.touches?.[0]?.clientY ?? lockedTouchStartY
      const deltaY = currentY - lockedTouchStartY

      if (!canScrollContainer(scrollContainer, deltaY)) {
        event.preventDefault()
      }

      lockedTouchStartY = currentY
    }

    const handleLockedWheel = (event) => {
      if (!document.body.classList.contains('offcanvas-menu-scroll-locked')) return

      const scrollContainer = getScrollableTouchContainer(event.target)
      if (!canScrollContainer(scrollContainer, -event.deltaY)) {
        event.preventDefault()
      }
    }

    // When navigating away from the menu (e.g. clicking a collection link) and coming back
    // via the browser back button, some browsers restore the previous DOM via bfcache.
    // In that case, the submenu state may be restored as "open", leaving a big gap under parent items.
    window.addEventListener('pageshow', (event) => {
      unlockPageScroll()
      resetOffcanvasMenuCollapses()
      clearOffcanvasMenuLayout()

      if (!event?.persisted) return

      if (offcanvasMenu?.classList.contains('show') && window.bootstrap?.Offcanvas) {
        const instance = window.bootstrap.Offcanvas.getInstance(offcanvasMenu) || new window.bootstrap.Offcanvas(offcanvasMenu)
        instance.hide()
      }
    })

    offcanvasMenu?.addEventListener('show.bs.offcanvas', () => {
      lockPageScroll()
      resetOffcanvasMenuCollapses()
      headerGroup?.classList.remove('hide')
      scheduleOffcanvasMenuLayoutSync()

      navbarMobile?.querySelector('.svg-icon-menu')?.classList.add('svg-icon-menu-close')
    })

    offcanvasMenu?.addEventListener('shown.bs.offcanvas', () => {
      scheduleOffcanvasMenuLayoutSync()
    })

    offcanvasMenu?.addEventListener('hide.bs.offcanvas', () => {
      navbarMobile?.querySelector('.svg-icon-menu')?.classList.remove('svg-icon-menu-close')
    })

    offcanvasMenu?.addEventListener('hidden.bs.offcanvas', () => {
      unlockPageScroll()
      resetOffcanvasMenuCollapses()
      scheduleOffcanvasMenuLayoutSync()
    })

    // When the offcanvas menu is open, Bootstrap traps focus inside it.
    // Ensure tapping/focusing the navbar search input closes the menu first
    // so the user can type their query on small screens.
    const mobileSearchInput = navbarMobile?.querySelector('form input[name="q"]')
    let focusAfterHide = false
    if (mobileSearchInput && offcanvasMenu) {
      const hideMenuForSearch = () => {
        const instance = window.bootstrap.Offcanvas.getInstance(offcanvasMenu) || new window.bootstrap.Offcanvas(offcanvasMenu)
        if (offcanvasMenu.classList.contains('show')) {
          focusAfterHide = true
          instance.hide()
        }
      }
      // Use early events to close before focus is trapped
      mobileSearchInput.addEventListener('pointerdown', hideMenuForSearch)
      mobileSearchInput.addEventListener('touchstart', hideMenuForSearch, { passive: true })
      mobileSearchInput.addEventListener('focus', hideMenuForSearch)

      offcanvasMenu.addEventListener('hidden.bs.offcanvas', () => {
        if (focusAfterHide) {
          mobileSearchInput.focus()
          focusAfterHide = false
        }
      })
    }

    const syncLayoutIfOpen = () => {
      if (!offcanvasMenu?.classList.contains('show')) return
      scheduleOffcanvasMenuLayoutSync()
    }

    const prepareOffcanvasMenuOpen = () => {
      if (offcanvasMenu?.classList.contains('show')) return
      lockPageScroll()
      headerGroup?.classList.remove('hide')
      scheduleOffcanvasMenuLayoutSync()
    }

    // Le verrou est posé dès le pointerdown pour éviter un saut à l'ouverture.
    // Si l'appui n'aboutit jamais à une ouverture (doigt qui glisse hors du
    // bouton, clic droit/milieu, geste abandonné), Bootstrap n'émet aucun
    // événement et la page resterait figée jusqu'au rechargement : on relâche.
    let menuOpening = false
    offcanvasMenu?.addEventListener('show.bs.offcanvas', () => { menuOpening = true })
    offcanvasMenu?.addEventListener('hidden.bs.offcanvas', () => { menuOpening = false })

    const releaseScrollLockIfNotOpening = () => {
      window.requestAnimationFrame(() => {
        if (menuOpening) return
        // `showing` couvre la transition d'ouverture (~300 ms) : sans ce test,
        // on déverrouillerait au milieu d'une ouverture légitime.
        if (offcanvasMenu?.classList.contains('show') || offcanvasMenu?.classList.contains('showing')) return
        unlockPageScroll()
      })
    }

    offcanvasMenuToggles.forEach((toggleEl) => {
      // ⚠️ Ne PAS verrouiller le scroll dès le pointerdown : le verrou fige
      // le body (position:fixed) et fait sauter scrollY à 0 EN PLEIN APPUI —
      // sur téléphone réel, un défilement pendant le toucher ANNULE le tap
      // (le click n'est jamais émis) : page défilée = menu qui ne s'ouvrait
      // jamais (constaté 2026-08-04). Le verrou est posé au CLICK : l'appui
      // est alors consommé, et ce gestionnaire (au niveau de l'élément)
      // s'exécute avant l'ouverture Bootstrap (déléguée au niveau document).
      toggleEl.addEventListener('pointerdown', (event) => {
        if (!documentElement.classList.contains('offcanvas-menu-ready')) {
          event.preventDefault()
        }
      })
      toggleEl.addEventListener('click', (event) => {
        if (!documentElement.classList.contains('offcanvas-menu-ready')) {
          event.preventDefault()
          return
        }

        menuOpening = false
        prepareOffcanvasMenuOpen()
        // Si l'ouverture Bootstrap n'aboutit pas (événement annulé par un
        // autre gestionnaire…), la page ne doit pas rester figée : contrôle
        // un instant plus tard, déverrouille si rien ne s'est ouvert.
        releaseScrollLockIfNotOpening()
      })
    })

    window.addEventListener('resize', scheduleOffcanvasMenuLayoutSync)
    window.addEventListener('orientationchange', scheduleOffcanvasMenuLayoutSync)
    window.addEventListener('load', scheduleOffcanvasMenuLayoutSync)
    window.visualViewport?.addEventListener('resize', syncLayoutIfOpen)
    window.visualViewport?.addEventListener('scroll', syncLayoutIfOpen)
    if (window.ResizeObserver && navbarMobile) {
      const navbarResizeObserver = new window.ResizeObserver(() => scheduleOffcanvasMenuLayoutSync())
      navbarResizeObserver.observe(navbarMobile)
      if (headerGroup) navbarResizeObserver.observe(headerGroup)
    }
    document.fonts?.ready?.then?.(() => scheduleOffcanvasMenuLayoutSync())
    scheduleOffcanvasMenuLayoutSync()
    window.requestAnimationFrame(() => {
      scheduleOffcanvasMenuLayoutSync()
      setOffcanvasMenuReadyState(true)
    })
  }

  enableHoverDropdowns () {
    const mq = window.matchMedia('(min-width: 1200px)')
    const container = this
    if (!container) return

    container.querySelectorAll('#navbar-desktop .dropdown').forEach((wrapper) => {
      const toggle = wrapper.querySelector('.dropdown-toggle')
      if (!toggle || !window.bootstrap || !window.bootstrap.Dropdown) return

      const instance = window.bootstrap.Dropdown.getInstance(toggle) || new window.bootstrap.Dropdown(toggle, { autoClose: 'outside' })

      let hideTimer

      const onEnter = () => {
        if (!mq.matches) return
        clearTimeout(hideTimer)
        instance.show()
        // Ensure background blur appears even if events are missed
        document.body.classList.add('navbar-dropdown-open')
      }
      const onLeave = () => {
        if (!mq.matches) return
        hideTimer = setTimeout(() => {
          instance.hide()
          // Remove blur if no dropdown remains open
          if (!document.querySelector('#navbar-desktop .dropdown-toggle.show')) {
            document.body.classList.remove('navbar-dropdown-open')
          }
        }, 150)
      }

      wrapper.addEventListener('mouseenter', onEnter)
      wrapper.addEventListener('mouseleave', onLeave)

      // Prevent click toggle on desktop, keep it for touch devices
      toggle.addEventListener('click', (e) => {
        if (!mq.matches) return
        e.preventDefault()
        e.stopPropagation()
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation()
        return false
      })

      // Allow keyboard access (Enter/Space) to toggle on desktop
      toggle.addEventListener('keydown', (e) => {
        if (!mq.matches) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          const isShown = wrapper.querySelector('.dropdown-menu')?.classList.contains('show')
          if (isShown) {
            instance.hide()
          } else {
            instance.show()
          }
        }
      })
    })

    // As a safety net, stop Bootstrap's delegated click handler in capture phase
    const stopClickOnDesktopToggles = (e) => {
      if (!mq.matches) return
      const target = e.target
      if (target && typeof target.closest === 'function' && target.closest('#navbar-desktop .dropdown .dropdown-toggle')) {
        e.preventDefault()
        e.stopPropagation()
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation()
      }
    }
    document.addEventListener('click', stopClickOnDesktopToggles, true)
  }

  guardHoverAfterSelectClose () {
    // À la fermeture d'une liste <select> native, Chrome/Edge ré-évaluent le
    // survol à la position du curseur sans mouvement réel de souris. Quand la
    // liste s'est dépliée vers le haut (champ en bas de fenêtre, ex. « Motif »
    // de la page Contact), le curseur retombe sur le menu du header : un
    // sous-menu s'ouvrait alors tout seul. La classe coupe le survol du menu
    // (pointer-events, custom.css) jusqu'à un vrai déplacement de la souris.
    const root = document.documentElement
    let anchor = null

    document.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLSelectElement)) return
      anchor = null
      root.classList.add('select-hover-guard')
    }, true)

    document.addEventListener('mousemove', (event) => {
      if (!root.classList.contains('select-hover-guard')) return
      // Le 1er mousemove peut être celui resynthétisé à la fermeture de la
      // liste : il ne prouve aucun mouvement réel, on ne fait que l'ancrer.
      if (!anchor) {
        anchor = { x: event.screenX, y: event.screenY }
        return
      }
      if (Math.abs(event.screenX - anchor.x) + Math.abs(event.screenY - anchor.y) > 3) {
        root.classList.remove('select-hover-guard')
      }
    }, { passive: true })
  }

  handleHeaderReveal () {
    let oldScroll = window.scrollY
    const headerGroup = document.querySelector('#header-group')
    if (!headerGroup) return

    window.addEventListener('scroll', () => {
      if (document.body.classList.contains('offcanvas-menu-scroll-locked')) {
        headerGroup.classList.remove('hide')
        oldScroll = Math.max(window.scrollY, 0)
        return
      }

      const newScroll = window.scrollY
      if (newScroll > oldScroll) {
        if (newScroll > headerGroup.clientHeight) {
          headerGroup.classList.add('hide')
        }
      } else if (newScroll < oldScroll) {
        headerGroup.classList.remove('hide')
      }

      oldScroll = Math.max(window.scrollY, 0)
    })
  }

  handleDropdownsBackdrop () {
    this.querySelectorAll('#navbar-desktop .dropdown-toggle').forEach(dropdown => {
      dropdown.addEventListener('hidden.bs.dropdown', () => {
        if (!document.querySelector('#navbar-desktop .dropdown-toggle.show')) {
          document.body.classList.remove('navbar-dropdown-open')
        }
      })
      dropdown.addEventListener('show.bs.dropdown', () => {
        document.body.classList.add('navbar-dropdown-open')
      })
    })
  }

  handleOffcanvasOnResize () {
    const offcanvasMenu = document.querySelector('#offcanvas-menu')
    if (!offcanvasMenu) return

    const documentElement = document.documentElement
    const mq = window.matchMedia('(min-width: 1200px)')

    const closeIfDesktop = () => {
      if (!mq.matches) return

      // Ensure an instance exists then hide if currently shown
      const instance = window.bootstrap.Offcanvas.getInstance(offcanvasMenu) || new window.bootstrap.Offcanvas(offcanvasMenu)
      if (offcanvasMenu.classList.contains('show')) {
        instance.hide()
      }
      // Clear any inline mobile layout set on open
      documentElement.style.removeProperty('--offcanvas-menu-top')
      offcanvasMenu.style.removeProperty('--offcanvas-menu-top')
      offcanvasMenu.style.top = ''
      offcanvasMenu.style.bottom = ''
      offcanvasMenu.style.height = ''
      offcanvasMenu.style.maxHeight = ''
    }

    if (mq.addEventListener) {
      mq.addEventListener('change', closeIfDesktop)
    } else if (mq.addListener) {
      // Safari/older browsers
      mq.addListener(closeIfDesktop)
    }

    // Run once on init in case of refreshed/resized states
    closeIfDesktop()
  }
}
customElements.define('navbar-wrapper', Navbar)
